/**
 * API client for Resource Hub endpoints
 * Based on OpenAPI contract (resources.openapi.yaml)
 */

import { apiClient } from '../../../core/api/client';
import type {
  ResourceListResponse,
  ResourceQueryParams,
} from '../types/filter.types';
import type { ResourceDetail, ResourceType, Tag, Category } from '../types/resource.types';

/**
 * Fetch resources with optional filters and pagination
 */
export const fetchResources = async (
  params?: ResourceQueryParams
): Promise<ResourceListResponse> => {
  const searchParams = new URLSearchParams();

  if (params?.page) {
    searchParams.append('page', params.page.toString());
  }
  if (params?.limit) {
    searchParams.append('limit', params.limit.toString());
  }
  if (params?.category && params.category.length > 0) {
    params.category.forEach((cat) => searchParams.append('category[]', cat));
  }
  if (params?.type && params.type.length > 0) {
    params.type.forEach((type) => searchParams.append('type[]', type));
  }
  if (params?.tag && params.tag.length > 0) {
    params.tag.forEach((tag) => searchParams.append('tag[]', tag));
  }
  if (params?.author && params.author.length > 0) {
    params.author.forEach((author) => searchParams.append('author[]', author));
  }
  if (params?.featured !== undefined) {
    searchParams.append('featured', params.featured.toString());
  }
  if (params?.recommended !== undefined) {
    searchParams.append('recommended', params.recommended.toString());
  }
  if (params?.favorited !== undefined) {
    searchParams.append('favorited', params.favorited.toString());
  }
  if (params?.search) {
    searchParams.append('search', params.search);
  }
  if (params?.sort) {
    searchParams.append('sort', params.sort);
  }
  if (params?.seed !== undefined) {
    searchParams.append('seed', params.seed.toString());
  }

  const queryString = searchParams.toString();
  const url = queryString ? `/resources?${queryString}` : '/resources';

  const response = await apiClient.get<ResourceListResponse>(url);
  return response.data;
};

/**
 * Fetch a single resource by seoUrl
 */
export const fetchResourceBySeoUrl = async (
  seoUrl: string
): Promise<ResourceDetail> => {
  const response = await apiClient.get<ResourceDetail>(`/resources/${seoUrl}`);
  return response.data;
};

/**
 * Fetch featured resources
 */
export const fetchFeaturedResources = async (
  limit: number = 5
): Promise<ResourceListResponse> => {
  const response = await apiClient.get<ResourceListResponse>(
    `/resources/featured?limit=${limit}`
  );
  return response.data;
};

/**
 * Fetch all categories for resource filtering
 */
export const fetchCategories = async (): Promise<Category[]> => {
  const response = await apiClient.get<Category[]>('/resources/categories');
  return response.data;
};

/**
 * Fetch all resource types
 */
export const fetchResourceTypes = async (): Promise<ResourceType[]> => {
  const response = await apiClient.get<ResourceType[]>('/resources/types');
  return response.data;
};

/**
 * Fetch all resource tags
 */
export const fetchResourceTags = async (): Promise<Tag[]> => {
  const response = await apiClient.get<Tag[]>('/resources/tags');
  return response.data;
};

/**
 * Add resource to favorites (authenticated)
 */
export const addFavorite = async (resourceId: string): Promise<void> => {
  await apiClient.post(`/resources/${resourceId}/favorite`);
};

/**
 * Remove resource from favorites (authenticated)
 */
export const removeFavorite = async (resourceId: string): Promise<void> => {
  await apiClient.delete(`/resources/${resourceId}/favorite`);
};
